import React, { useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Box, TextField, InputAdornment, MenuItem } from '@mui/material';
import Paper from '@mui/material/Paper';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import {
    ExpandLess,
    ExpandMore,
    Search as SearchIcon,
} from '@mui/icons-material';
import Grid from '@mui/material/Grid';
import { AppContext } from '../AppContext';
import { SttPagination } from '../SttPagination';
import { SttDialog } from './SttDialog';
import { SttActivityFillForms } from './SttActivityFillForms';
import { SttList } from '../lists/SttList';
import { SPORTCENTER_ACTIVITIES_STUDENTS } from '../../lib/queryKeys';
import { format as formatDate } from 'date-fns';

type Rate = {
    id: number;
    name: string;
};

type Activity = {
    id: number;
    name: string;
    rates?: Rate[];
    form?: ActivityForm[];
};

type ActivityForm = {
    id: string | number;
    answer?: {
        value: unknown;
    };
};

type ActivitiesResponse = {
    rows: Activity[];
    count: number;
};

type FilterState = {
    name: string;
    year: string;
};

type Props = {
    open?: boolean;
    studentId?: number;
    onClose?: () => void;
};

/**
 * Rate selector ordered by activities.
 * @param open boolean Open or close the dialog.
 * @param studentId integer Student id.
 * @param onClose function Method threw when user close the dialog.
 * @returns {React.ReactElement} JSX with component.
 */
export function SttSelectClassAndRateDialog({
    open = false,
    studentId,
    onClose,
}: Props): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [activities, setActivities] = useState<ActivitiesResponse>({
        rows: [],
        count: 0,
    });
    const [searchInput, setSearchInput] = useState('');
    const [filter, setFilter] = useState<FilterState>({
        name: '',
        year: formatDate(new Date(), 'yyyy'),
    });
    const [openListItem, setOpenListItem] = React.useState<boolean[]>([]);
    const [page, setPage] = useState<number>(1);
    const rowsPerPage = 7;
    const [activityForms, setActivityForms] = useState<ActivityForm[]>([]);
    const [openActivityFillFormsDialog, setOpenActivityFillFormsDialog] =
        useState(false);
    const [selectedRateId, setSelectedRateId] = useState<number>();
    const queryClient = useQueryClient();

    /**
     * Will be load activity forms data and next open fill forms dialog or call changeStudentRate.
     * @param activityId Activity ID.
     * @param rateId Rate ID.
     */
    const changeStudentRateProccess = (activityId: number, rateId: number) => {
        cxt?.api('GET', `/activities/${activityId}`, {
            params: {
                trash: true,
            },
            success: (response: any) => {
                if (response?.activity?.form) {
                    setActivityForms(response.activity.form);
                    setOpenActivityFillFormsDialog(true);
                    setSelectedRateId(rateId);
                } else {
                    changeStudentRate(rateId);
                }
            },
        });
    };

    /**
     * Load all activities using sport center id.
     * @param p Search parameters ({page: X, rows: X})
     */
    const loadActivities = (
        p: { page: number; rows: number } = { page: 1, rows: 7 },
    ) => {
        cxt?.api('GET', `/scs/${cxt?.sc?.id}/activities`, {
            params: {
                name: filter.name,
                year: parseInt(filter.year, 10),
                ...p,
            },
            success: (response: any) => {
                setActivities(response);
                setOpenListItem(new Array(response.rows.length).fill(false));
            },
        });
    };

    /**
     * Load all fees using activity id.
     * @param activityId Activity id.
     */
    const loadFeesByActivity = (activityId: number) => {
        cxt?.api('GET', `/activities/${activityId}`, {
            success: (response: any) => {
                const newActivities = { ...activities };
                const activityIndex = newActivities.rows.findIndex(
                    (x: any) => x.id === activityId,
                );
                newActivities.rows[activityIndex] = response.activity;
                handleClick(activityIndex);
                setActivities(newActivities);
            },
        });
    };

    /**
     * Change student rate.
     * @param idRate integer Rate ID.
     * @param p Params.
     */
    const changeStudentRate = (
        idRate: number,
        p: Record<string, unknown> = {},
    ) => {
        cxt?.api('PUT', `/students/${studentId}/changeRate`, {
            params: {
                idRate,
                ...p,
            },
            success: (_r: unknown) => {
                if (onClose) onClose();
                queryClient.invalidateQueries({
                    queryKey: [SPORTCENTER_ACTIVITIES_STUDENTS],
                });
                setOpenActivityFillFormsDialog(false);
                cxt?.showMessage('S', t('Changed'));
            },
        });
    };

    /**
     * Will be execute when user press cell list item.
     * Save the status of the list to check if the activity was loaded or not.
     * @param index integer Activity index.
     */
    const handleClick = (index: number) => {
        const newOpenListItem = [...openListItem];
        newOpenListItem[index] = !newOpenListItem[index];
        setOpenListItem(newOpenListItem);
    };

    /**
     * Render activity cell inside SttList.
     * @param activity Activity object.
     * @param index Activity index.
     * @returns {React.ReactElement} JSX with activity cell.
     */
    const renderCell = (
        activity: Activity,
        index: number,
    ): React.ReactElement => (
        <>
            <ListItemButton
                onClick={() => {
                    if (activity.hasOwnProperty('rates')) {
                        handleClick(index);
                    } else {
                        loadFeesByActivity(activity.id);
                    }
                }}
            >
                <ListItemText primary={activity.name} />
                {openListItem[index] ? <ExpandLess /> : <ExpandMore />}
            </ListItemButton>
            <Collapse in={openListItem[index]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    {activity.rates?.map((rate: any) => (
                        <ListItemButton
                            key={rate.id}
                            onClick={() =>
                                changeStudentRateProccess(activity.id, rate.id)
                            }
                        >
                            <ListItemText primary={rate.name} />
                        </ListItemButton>
                    ))}
                </List>
            </Collapse>
        </>
    );

    /**
     * Will be execute when change page.
     * @param page integer Page number.
     */
    const onChangePage = (page: number) => {
        setPage(page);
        loadActivities({ page, rows: rowsPerPage });
    };

    /**
     * When the component is opened we load activities.
     */
    useEffect(() => {
        if (open) loadActivities();
    }, [open]);

    /**
     * When filter changes (year selector or name search via Enter) we reload activities.
     */
    useEffect(() => {
        if (open) {
            setPage(1);
            loadActivities();
        }
    }, [filter.year, filter.name]);

    // -----| Render |-----
    return (
        <>
            <SttDialog
                title={t('ChangeClassAndRate')}
                open={open}
                onClose={() => (onClose ? onClose() : null)}
                maxWidth="md"
                content={
                    <>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid size={{ xs: 12, sm: 8 }}>
                                <TextField
                                    placeholder={t('Search')}
                                    size="small"
                                    fullWidth
                                    value={searchInput}
                                    onChange={(e) =>
                                        setSearchInput(e.target.value)
                                    }
                                    onKeyUp={(e) => {
                                        if (e.key === 'Enter') {
                                            setFilter({
                                                ...filter,
                                                name: searchInput,
                                            });
                                            setPage(1);
                                        }
                                    }}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment
                                                    position="start"
                                                    className="cursor-pointer"
                                                    onClick={() => {
                                                        setFilter({
                                                            ...filter,
                                                            name: searchInput,
                                                        });
                                                        setPage(1);
                                                    }}
                                                >
                                                    <SearchIcon />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 4 }}>
                                <TextField
                                    select
                                    label={t('Year')}
                                    size="small"
                                    fullWidth
                                    value={filter.year}
                                    onChange={(e) => {
                                        setFilter({
                                            ...filter,
                                            year: e.target.value,
                                        });
                                        setPage(1);
                                    }}
                                >
                                    {(() => {
                                        const currentYear =
                                            new Date().getFullYear();
                                        const years = Array.from(
                                            { length: 7 },
                                            (_, i) => currentYear - 5 + i,
                                        );
                                        return years.map((year) => (
                                            <MenuItem
                                                key={year}
                                                value={year.toString()}
                                            >
                                                {year}
                                            </MenuItem>
                                        ));
                                    })()}
                                </TextField>
                            </Grid>
                        </Grid>

                        <Box
                            display="flex"
                            justifyContent="flex-end"
                            sx={{ mb: 2 }}
                        >
                            <SttPagination
                                page={page}
                                pages={Math.ceil(
                                    activities.count / rowsPerPage,
                                )}
                                count={activities.count}
                                onChangePage={(page: number) =>
                                    onChangePage(page)
                                }
                            />
                        </Box>

                        <Box mt={2}>
                            <Paper>
                                <SttList
                                    data={
                                        activities.rows &&
                                        activities.rows.map(
                                            (activity, index) => ({
                                                caption: renderCell(
                                                    activity,
                                                    index,
                                                ),
                                            }),
                                        )
                                    }
                                />
                            </Paper>
                        </Box>
                    </>
                }
            />
            <SttActivityFillForms
                open={openActivityFillFormsDialog}
                onClose={() => setOpenActivityFillFormsDialog(false)}
                onSave={(
                    rateId: string | number,
                    params: { form: Record<string, unknown> },
                ) =>
                    changeStudentRate(
                        rateId as number,
                        params as Record<string, unknown>,
                    )
                }
                rateId={selectedRateId!}
                activityForms={activityForms}
            />
        </>
    );
}
